import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Eye, Download, Upload, Bell, FileText, ChevronRight, Paperclip, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { typeLabels, statusIcons, statusColors } from './saleConstants';
import EmlViewerDialog from '../shared/EmlViewerDialog';

const EMAIL_DELIVERABLE_TYPES = ['hip_number_email', 'ship_in_note_email', 'seller_thank_you'];

const OpportunityDeliverablesTab = ({
  id,
  opportunity,
  deliverables,
  saleOwnerDeliverables,
  owners,
  isReadOnly,
  onDeliverableClick,
  onPreviewFile,
  onBulkUpload,
  onBatchReminder,
}) => {
  const navigate = useNavigate();
  const [emlViewFileId, setEmlViewFileId] = useState(null);
  const [emlViewSentAt, setEmlViewSentAt] = useState(null);

  const handleDownload = async (fileUrl, fileName) => {
    if (!fileUrl) { toast.error('No file URL'); return; }
    try {
      const normalizedUrl = fileUrl.startsWith('/') ? fileUrl.substring(1) : fileUrl;
      const response = await api.get(normalizedUrl, { responseType: 'blob' });
      const blob = response.data;
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      toast.success('Downloaded');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Download failed');
    }
  };

  const handleDownloadEml = (fileId, filename) => {
    const token = localStorage.getItem('token');
    fetch(`${window.location.origin}/api/files/download/${fileId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'email.eml';
      a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error('Download failed'));
  };

  const handleDownloadAll = async () => {
    const ownerIds = opportunity?.owner_crm_entity_ids || [];
    const horseDeliverablesWithFiles = deliverables.filter(d => d.file_url);
    const ownerDeliverablesWithFiles = saleOwnerDeliverables.filter(d =>
      d.file_url && ownerIds.includes(d.owner_crm_entity_id)
    );
    const allDeliverablesWithFiles = [...horseDeliverablesWithFiles, ...ownerDeliverablesWithFiles];

    if (allDeliverablesWithFiles.length === 0) { toast.error('No files to download'); return; }
    toast.info(`Downloading ${allDeliverablesWithFiles.length} file(s)...`);

    let successCount = 0;
    let failCount = 0;
    for (const deliverable of allDeliverablesWithFiles) {
      try {
        const fileUrl = deliverable.file_url;
        const fileName = deliverable.file_name || `deliverable-${deliverable.id}`;
        const normalizedUrl = fileUrl.startsWith('/') ? fileUrl.substring(1) : fileUrl;
        const response = await api.get(normalizedUrl, { responseType: 'blob' });
        const blob = response.data;
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
        successCount++;
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (err) {
        console.error('Download error:', err);
        failCount++;
      }
    }
    if (successCount > 0) toast.success(`Successfully downloaded ${successCount} file(s)`);
    if (failCount > 0) toast.error(`Failed to download ${failCount} file(s)`);
  };

  const getFileCount = () => {
    const ownerIds = opportunity?.owner_crm_entity_ids || [];
    const horseCount = deliverables.filter(d => d.file_url).length;
    const ownerCount = saleOwnerDeliverables.filter(d => d.file_url && ownerIds.includes(d.owner_crm_entity_id)).length;
    return horseCount + ownerCount;
  };

  if (deliverables.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground/20" />
          <h3 className="font-serif text-lg font-semibold mb-2">No Deliverables Selected</h3>
          <p className="text-muted-foreground mb-6">Select which deliverables you need to track for this sale entry</p>
          <Button onClick={() => navigate(`/sales/opportunities/${id}/select-deliverables`)} className="bg-primary hover:bg-primary/90 text-white w-full sm:w-auto">
            <FileText className="w-4 h-4 mr-2" />
            Manage Deliverables
          </Button>
        </CardContent>
      </Card>
    );
  }

  const fileCount = getFileCount();

  return (
    <>
      {/* Action Buttons — stacked on mobile */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Button variant="outline" onClick={handleDownloadAll} disabled={fileCount === 0} className="w-full sm:w-auto">
          <Download className="w-4 h-4 mr-2" />
          Download All ({fileCount})
        </Button>
        {!isReadOnly && (
          <>
            <Button className="bg-primary hover:bg-primary/90 text-white w-full sm:w-auto" onClick={onBulkUpload}>
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button variant="outline" onClick={onBatchReminder} className="w-full sm:w-auto">
              <Bell className="w-4 h-4 mr-2" />
              Send Reminders
            </Button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Horse Deliverables */}
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <CardTitle className="font-serif text-lg">Horse Deliverables</CardTitle>
              <CardDescription>Required documents and media for the horse</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate(`/sales/opportunities/${id}/select-deliverables`)} className="w-full sm:w-auto">
              <FileText className="w-4 h-4 mr-2" />
              Manage Deliverables
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {deliverables
                .filter(d => d.deliverable_type_horse)
                .map((deliverable, index) => {
                  const status = deliverable.status || 'not_started';
                  const StatusIcon = statusIcons[status] || statusIcons['not_started'];
                  const statusColor = statusColors[status] || statusColors['not_started'];
                  const label = typeLabels[deliverable.deliverable_type_horse] || deliverable.deliverable_type_horse;

                  return (
                    <div
                      key={deliverable.id || index}
                      className="flex items-center justify-between p-3 border border-border hover:bg-muted/30 transition-colors cursor-pointer"
                      data-testid={`deliverable-${deliverable.deliverable_type_horse}`}
                      onClick={() => onDeliverableClick(deliverable)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <StatusIcon className={`w-5 h-5 ${statusColor}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{label}</p>
                            {deliverable.is_expired && (
                              <Badge variant="destructive" className="text-xs">Expired</Badge>
                            )}
                            {!deliverable.is_expired && deliverable.days_until_expiry !== undefined && deliverable.days_until_expiry < 30 && deliverable.days_until_expiry > 0 && (
                              <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                                Expires in {deliverable.days_until_expiry} days
                              </Badge>
                            )}
                          </div>
                          {deliverable.nickname && (
                            <p className="text-xs text-muted-foreground">{deliverable.nickname}</p>
                          )}
                          {deliverable.file_name && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Paperclip className="w-3 h-3" />
                              {deliverable.file_name}
                            </p>
                          )}
                        </div>
                      </div>
                      {deliverable.file_url && !EMAIL_DELIVERABLE_TYPES.includes(deliverable.deliverable_type_horse) && (
                        <div className="flex gap-2 ml-2" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onPreviewFile(deliverable, label); }} title="View File">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDownload(deliverable.file_url, deliverable.file_name); }} title="Download File">
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      {EMAIL_DELIVERABLE_TYPES.includes(deliverable.deliverable_type_horse) && deliverable.emailed_eml_file_id && (
                        <div className="flex gap-2 ml-2" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEmlViewFileId(deliverable.emailed_eml_file_id); setEmlViewSentAt(deliverable.emailed_at); }} title="View Sent Email">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDownloadEml(deliverable.emailed_eml_file_id, `${deliverable.deliverable_type_horse}.eml`); }} title="Download .eml">
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      <ChevronRight className="w-4 h-4 text-muted-foreground ml-2" />
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>

        {/* Owner Deliverables */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-lg">Owner Deliverables</CardTitle>
            <CardDescription>Documents required from each owner</CardDescription>
          </CardHeader>
          <CardContent>
            {owners.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <User className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>No owners linked</p>
              </div>
            ) : (
              <div className="space-y-4">
                {owners.map((owner) => {
                  const ownerSaleDeliverables = saleOwnerDeliverables.filter(d => d.owner_crm_entity_id === owner.id);
                  // Tenant owners (Consignor) only need 2 deliverable types
                  const allOwnerTypes = owner.is_tenant_owner ? [
                    { key: 'sale_company_contract', label: 'Sale Company Contract' },
                    { key: 'sale_authorization', label: 'Sale Authorization' },
                  ] : [
                    { key: 'sale_company_contract', label: 'Sale Company Contract' },
                    { key: 'consignment_contract', label: 'Consignment Contract' },
                    { key: 'sale_authorization', label: 'Sale Authorization' },
                    { key: 'hip_number_email', label: 'Hip Number Email' },
                    { key: 'ship_in_note_email', label: 'Ship-in Note Email' }
                  ];

                  return (
                    <div key={owner.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        {owner.entity_type === 'company' ? (
                          <Building2 className="w-5 h-5 text-primary" />
                        ) : (
                          <User className="w-5 h-5 text-primary" />
                        )}
                        <p className="font-medium">{owner.system_name}</p>
                      </div>
                      <div className="space-y-2">
                        {allOwnerTypes.map((delType) => {
                          const deliverablesOfType = ownerSaleDeliverables.filter(d => d.deliverable_type === delType.key);

                          if (deliverablesOfType.length === 0) {
                            const NotStartedIcon = statusIcons['not_started'];
                            return (
                              <div key={delType.key} className="flex items-center justify-between p-3 border border-border rounded-lg bg-muted/20">
                                <div className="flex items-center gap-3 flex-1">
                                  <NotStartedIcon className={`w-5 h-5 ${statusColors['not_started']}`} />
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{delType.label}</p>
                                    <p className="text-xs text-muted-foreground">Not created yet</p>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          return deliverablesOfType.map((deliverable, idx) => {
                            const status = deliverable.status || 'not_started';
                            const StatusIcon = statusIcons[status] || statusIcons['not_started'];
                            const statusColor = statusColors[status] || statusColors['not_started'];

                            return (
                              <div
                                key={`${delType.key}-${idx}`}
                                className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/30 transition-colors cursor-pointer"
                                onClick={() => onDeliverableClick(deliverable)}
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <StatusIcon className={`w-5 h-5 ${statusColor}`} />
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">
                                      {delType.label}
                                      {deliverable.property_line && ` (${deliverable.property_line})`}
                                    </p>
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                      {/* Deliverable status flags */}
                                      {delType.key === 'consignment_contract' && deliverable.contract_internally_signed && (
                                        <Badge className="text-[10px] py-0 px-1.5 bg-blue-100 text-blue-700 pointer-events-none">Signed Internally</Badge>
                                      )}
                                      {delType.key === 'consignment_contract' && deliverable.contract_status === 'awaiting_internal_signature' && !deliverable.contract_internally_signed && (
                                        <Badge className="text-[10px] py-0 px-1.5 bg-yellow-100 text-yellow-700 pointer-events-none">Awaiting Internal Sig.</Badge>
                                      )}
                                      {delType.key === 'sale_authorization' && deliverable.executed_file_id && (
                                        <Badge className="text-[10px] py-0 px-1.5 bg-green-100 text-green-700 pointer-events-none">Owner Signed</Badge>
                                      )}
                                      {delType.key === 'consignment_contract' && deliverable.executed_file_id && (
                                        <Badge className="text-[10px] py-0 px-1.5 bg-green-100 text-green-700 pointer-events-none">Owner Signed</Badge>
                                      )}
                                      {delType.key === 'questionnaire' && (deliverable.completed_file_id || deliverable.questionnaire_answers) && (
                                        <Badge className="text-[10px] py-0 px-1.5 bg-green-100 text-green-700 pointer-events-none">Completed</Badge>
                                      )}
                                    </div>
                                    {deliverable.file_name && (
                                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Paperclip className="w-3 h-3" />
                                        {deliverable.file_name}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {deliverable.file_url && !EMAIL_DELIVERABLE_TYPES.includes(delType.key) && (
                                  <div className="flex gap-2 ml-2" onClick={(e) => e.stopPropagation()}>
                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onPreviewFile(deliverable, delType.label); }} title="View File">
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDownload(deliverable.file_url, deliverable.file_name); }} title="Download File">
                                      <Download className="w-4 h-4" />
                                    </Button>
                                  </div>
                                )}
                                {EMAIL_DELIVERABLE_TYPES.includes(delType.key) && deliverable.emailed_eml_file_id && (
                                  <div className="flex gap-2 ml-2" onClick={(e) => e.stopPropagation()}>
                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEmlViewFileId(deliverable.emailed_eml_file_id); setEmlViewSentAt(deliverable.emailed_at); }} title="View Sent Email">
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDownloadEml(deliverable.emailed_eml_file_id, `${delType.key}.eml`); }} title="Download .eml">
                                      <Download className="w-4 h-4" />
                                    </Button>
                                  </div>
                                )}
                                <ChevronRight className="w-4 h-4 text-muted-foreground ml-2" />
                              </div>
                            );
                          });
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <EmlViewerDialog
        open={!!emlViewFileId}
        onClose={() => { setEmlViewFileId(null); setEmlViewSentAt(null); }}
        fileId={emlViewFileId}
        sentAt={emlViewSentAt}
        title="Sent Email Preview"
      />
    </>
  );
};

export default OpportunityDeliverablesTab;
